<template>
	<div class="ms_wrapper" ref="root">
		<button type="button"
			class="ms_trigger"
			:class="{ 'is-active': isActive, 'is-open': isOpen }"
			:aria-expanded="isOpen ? 'true' : 'false'"
			aria-haspopup="listbox"
			@click.stop="toggle">
			<span class="ms_label">{{ placeholder }}</span>
			<span v-if="value.length > 0" class="ms_count_badge">{{ value.length }}</span>
			<ArrowDown class="ms_chevron" :class="{ 'is-rotated': isOpen }" />
		</button>

		<transition name="ms_fade">
			<div v-if="isOpen" class="ms_panel"
				:class="{ 'is-right': align === 'right' }"
				role="listbox" aria-multiselectable="true" @click.stop>
				<div v-if="searchable" class="ms_search">
					<input ref="search"
						v-model="query"
						type="text"
						class="ms_search_input"
						:placeholder="searchPlaceholder"
						@click.stop />
				</div>
				<ul class="ms_options">
					<li v-for="opt in visibleOptions" :key="opt.id"
						class="ms_option"
						:class="{ 'is-checked': isSelected(opt.id) }"
						role="option"
						:aria-selected="isSelected(opt.id) ? 'true' : 'false'"
						:title="opt.label"
						@click.stop="toggleOption(opt.id)">
						<span class="ms_check" :class="{ 'is-checked': isSelected(opt.id) }">
							<span class="ms_check_mark" v-if="isSelected(opt.id)"></span>
						</span>
						<span class="ms_option_label">{{ opt.label }}</span>
					</li>
					<li v-if="visibleOptions.length === 0" class="ms_empty">
						Sin resultados
					</li>
				</ul>
				<p v-if="hiddenCount > 0" class="ms_overflow_hint">
					Mostrando {{ visibleOptions.length }} de {{ matchedCount }} — refiná tu búsqueda
				</p>
				<div v-if="value.length > 0" class="ms_footer">
					<button type="button"
						class="ms_clear_btn"
						@click.stop="clearAll">
						{{ clearLabel }}
					</button>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import ArrowDown from "@/components/svg/ArrowDown.vue";

export default {
	name: "MultiSelectDropdown",

	components: { ArrowDown },

	model: {
		prop: "value",
		event: "input",
	},

	props: {
		value: {
			type: Array,
			default: () => [],
		},
		options: {
			type: Array,
			required: true,
			validator: (arr) => arr.every(o => o && o.id !== undefined && o.label !== undefined),
		},
		placeholder: {
			type: String,
			default: "Seleccionar",
		},
		clearLabel: {
			type: String,
			default: "Ver todos",
		},
		// Opt-in: agrega un input de búsqueda arriba de la lista. Pensado para
		// listas largas (cientos/miles de items, p.ej. clientes). Los call sites
		// existentes (sin esta prop) renderean igual que antes.
		searchable: {
			type: Boolean,
			default: false,
		},
		searchPlaceholder: {
			type: String,
			default: "Buscar…",
		},
		// Tope de items renderados a la vez. Con listas de miles, renderear todos
		// los <li> traba el dropdown; capeamos y guiamos al usuario a afinar la
		// búsqueda. El filtrado corre sobre el set completo, sólo se acota el
		// render.
		maxVisible: {
			type: Number,
			default: 200,
		},
		// Lado al que se ancla el panel desplegado: "left" (default) abre desde
		// el borde izquierdo del trigger; "right" desde el derecho. Útil cuando
		// el trigger vive pegado al borde derecho y un panel left-anclado se
		// saldría de pantalla.
		align: {
			type: String,
			default: "left",
			validator: (v) => ["left", "right"].includes(v),
		},
	},

	data() {
		return { isOpen: false, query: "" };
	},

	computed: {
		isActive() { return this.value.length > 0; },

		// Precomputa la forma normalizada (sin acentos, lowercase) de cada label
		// una sola vez por cambio de `options`. Filtrar miles de items por
		// keystroke se vuelve un substring-check sobre strings ya normalizados.
		normalizedOptions() {
			return this.options.map((opt) => ({
				opt,
				norm: this.normalize(String(opt.label)),
			}));
		},

		// Items que matchean la query (set completo, sin capear).
		matchedOptions() {
			const q = this.normalize(this.query.trim());
			if (!q) return this.options;
			return this.normalizedOptions
				.filter((entry) => entry.norm.includes(q))
				.map((entry) => entry.opt);
		},

		matchedCount() { return this.matchedOptions.length; },

		// Slice renderado — capeado a `maxVisible`.
		visibleOptions() {
			return this.matchedOptions.slice(0, this.maxVisible);
		},

		hiddenCount() {
			return Math.max(0, this.matchedCount - this.visibleOptions.length);
		},
	},

	methods: {
		// Normalización compartida: descompone acentos (NFD), descarta las marcas
		// diacríticas y baja a minúscula. "Lácteos S.A." → "lacteos s.a.".
		normalize(str) {
			return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
		},
		isSelected(id) { return this.value.includes(id); },
		toggle() {
			if (this.isOpen) this.close();
			else this.open();
		},
		open() {
			if (this.isOpen) return;
			this.isOpen = true;
			this.query = "";
			this.$nextTick(() => {
				document.addEventListener("mousedown", this.handleClickOutside);
				if (this.searchable && this.$refs.search) this.$refs.search.focus();
			});
		},
		close() {
			if (!this.isOpen) return;
			this.isOpen = false;
			document.removeEventListener("mousedown", this.handleClickOutside);
		},
		toggleOption(id) {
			const idx = this.value.indexOf(id);
			const next = idx === -1
				? [...this.value, id]
				: this.value.filter(v => v !== id);
			this.$emit("input", next);
		},
		clearAll() {
			this.$emit("input", []);
			// Limpiar también cierra el panel: el usuario terminó con el filtro,
			// no lo dejamos abierto sobre una lista sin selección.
			this.close();
		},
		handleClickOutside(event) {
			const root = this.$refs.root;
			if (root && !root.contains(event.target)) {
				this.close();
			}
		},
	},

	beforeDestroy() {
		document.removeEventListener("mousedown", this.handleClickOutside);
	},
};
</script>

<style scoped lang="scss">
.ms_wrapper {
	position: relative;
	display: inline-flex;
}

.ms_trigger {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 0 12px 0 16px;
	min-height: 34px;
	background: #ffffff;
	border: 1px solid var(--report-border, #e5e7eb);
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: #4b5563;
	cursor: pointer;
	transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
	user-select: none;
	box-sizing: border-box;

	&:hover { background: #f9fafb; }

	&.is-active {
		background: var(--report-primary, #00A9AD);
		border-color: var(--report-primary, #00A9AD);
		color: #ffffff;
	}

	&.is-open {
		border-color: var(--report-primary, #00A9AD);
	}
}

.ms_label { line-height: 1; }

.ms_count_badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 20px;
	height: 20px;
	padding: 0 6px;
	background: rgba(255, 255, 255, 0.22);
	border-radius: 10px;
	font-size: 12px;
	font-weight: 700;
	line-height: 1;

	.ms_trigger:not(.is-active) & {
		background: var(--report-primary, #00A9AD);
		color: #ffffff;
	}
}

.ms_chevron {
	width: 12px;
	height: 12px;
	transition: transform 0.2s ease;
	color: inherit;

	&.is-rotated { transform: rotate(180deg); }
}

.ms_panel {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	min-width: 220px;

	// Anclaje a la derecha: el panel crece hacia la izquierda desde el borde
	// derecho del trigger, para no salirse de pantalla cuando el trigger está
	// pegado al borde derecho.
	&.is-right {
		left: auto;
		right: 0;
	}
	background: #ffffff;
	border: 1px solid var(--report-border, #e5e7eb);
	border-radius: 10px;
	box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
	padding: 6px;
	z-index: 4;
}

.ms_search {
	padding: 2px 2px 6px;
}

.ms_search_input {
	width: 100%;
	padding: 7px 10px;
	border: 1px solid var(--report-border, #e5e7eb);
	border-radius: 6px;
	font-size: 14px;
	color: #4b5563;
	outline: none;
	box-sizing: border-box;
	transition: border-color 0.12s ease;

	&:focus { border-color: var(--report-primary, #00A9AD); }
}

.ms_options {
	list-style: none;
	margin: 0;
	padding: 0;
	// Listas largas (clientes) scrollean dentro del panel en vez de empujar la
	// página. ~7 items visibles antes del scroll.
	max-height: 260px;
	overflow-y: auto;
}

.ms_empty {
	padding: 8px 10px;
	color: #9ca3af;
	font-size: 13px;
	user-select: none;
}

.ms_overflow_hint {
	margin: 2px 2px 0;
	padding: 6px 10px;
	color: #9ca3af;
	font-size: 12px;
	line-height: 1.3;
	border-top: 1px solid var(--report-border, #e5e7eb);
}

.ms_option {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 10px;
	border-radius: 6px;
	cursor: pointer;
	color: #4b5563;
	font-size: 14px;
	font-weight: 500;
	transition: background 0.12s ease, color 0.12s ease;
	user-select: none;

	&:hover { background: #f9fafb; }

	&.is-checked { color: var(--report-primary, #00A9AD); }
}

.ms_check {
	flex-shrink: 0;
	width: 16px;
	height: 16px;
	border: 1.5px solid #cbd5e1;
	border-radius: 4px;
	background: #ffffff;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: border-color 0.12s ease, background 0.12s ease;

	&.is-checked {
		border-color: var(--report-primary, #00A9AD);
		background: var(--report-primary, #00A9AD);
	}
}

.ms_check_mark {
	width: 8px;
	height: 4px;
	border-left: 2px solid #ffffff;
	border-bottom: 2px solid #ffffff;
	transform: rotate(-45deg) translate(1px, -1px);
}

.ms_option_label {
	line-height: 1.2;
	// Nombres largos (razones sociales) no rompen el ancho del panel: una línea
	// con ellipsis, el `title` del <li> muestra el nombre completo en hover.
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

// Con buscador la lista puede ser ancha (nombres de clientes); damos un cap
// para que el panel no se desborde de la pantalla.
.ms_panel { max-width: 360px; }

// Zona de acción separada de la lista por un divisor; el botón vive dentro con
// su propio chrome para leerse como control presionable, no como link de pie.
.ms_footer {
	margin-top: 6px;
	padding-top: 8px;
	border-top: 1px solid var(--report-border, #e5e7eb);
}

.ms_clear_btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	padding: 8px 12px;
	// Inset sobre el panel blanco: surface gris + borde = afordancia de botón.
	background: var(--report-surface, #f9fafb);
	border: 1px solid var(--report-border, #e5e7eb);
	border-radius: 8px;
	font-size: 13px;
	font-weight: 600;
	color: #4b5563;
	cursor: pointer;
	transition: background 0.12s ease, border-color 0.12s ease,
		color 0.12s ease, transform 0.06s ease;

	&:hover {
		background: #ffffff;
		border-color: var(--report-primary, #00A9AD);
		color: var(--report-primary, #00A9AD);
	}

	// El translate de 1px es la señal de "presionable": el botón se hunde al
	// click. Micro-interacción, sin bounce.
	&:active {
		transform: translateY(1px);
		background: var(--report-surface, #f3f4f6);
	}

	&:focus-visible {
		outline: none;
		border-color: var(--report-primary, #00A9AD);
		box-shadow: 0 0 0 3px rgba(0, 169, 173, 0.18);
	}
}

.ms_fade-enter-active,
.ms_fade-leave-active {
	transition: opacity 0.12s ease, transform 0.12s ease;
}

.ms_fade-enter,
.ms_fade-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}
</style>
